/**
 * Per-station exclusive lock with CAS-style acquire via the TabLockRepository.
 *
 * Two roles:
 *   1. Owner: tryAcquire → start heartbeat → release (or be reaped on crash).
 *   2. Observer: subscribe to TabBroadcast to learn about acquire/release in
 *      other tabs without polling Dexie.
 *
 * Timing constants from data/storage-schema.yaml#tabLocks.cross_tab.
 */
import type { TabLockRepository, TabLockRow } from '@/persistence/ports';
import type { TabBroadcast } from './broadcast';

export const DEFAULT_HEARTBEAT_INTERVAL_MS = 3_000;
export const DEFAULT_STALE_THRESHOLD_MS = 10_000;

export interface CrossTabLockOptions {
  readonly cpid: string;
  readonly wsUrl: string;
  readonly tabId: string;
  readonly heartbeatIntervalMs?: number;
  readonly now?: () => number;
  readonly userAgent?: string;
  readonly broadcast?: TabBroadcast;
}

export interface AcquireResult {
  readonly acquired: boolean;
  readonly holder?: TabLockRow;
}

export class CrossTabLock {
  readonly #repo: TabLockRepository;
  readonly #cpid: string;
  readonly #wsUrl: string;
  readonly #tabId: string;
  readonly #intervalMs: number;
  readonly #now: () => number;
  readonly #userAgent: string | undefined;
  readonly #broadcast: TabBroadcast | undefined;
  #heartbeatHandle: ReturnType<typeof setInterval> | null = null;
  #held = false;

  constructor(repo: TabLockRepository, opts: CrossTabLockOptions) {
    this.#repo = repo;
    this.#cpid = opts.cpid;
    this.#wsUrl = opts.wsUrl;
    this.#tabId = opts.tabId;
    this.#intervalMs = opts.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS;
    this.#now = opts.now ?? (() => Date.now());
    this.#userAgent = opts.userAgent;
    this.#broadcast = opts.broadcast;
  }

  get held(): boolean {
    return this.#held;
  }

  /**
   * Attempt to take the lock.
   *
   * Self-takeover: if the existing row's `tabId` matches OUR `tabId` (which
   * happens after a page refresh in the same tab — sessionStorage preserved
   * the id but the old lock row was not released cleanly), we release the
   * stale row as its rightful owner and retry. This is the cold path; the
   * hot path is the single-write happy path.
   *
   * Returns the existing holder on failure when the holder is a different tab.
   */
  async tryAcquire(): Promise<AcquireResult> {
    const ok = await this.#tryInsertOwnRow();
    if (ok) {
      this.#held = true;
      this.#broadcast?.post({
        type: 'LOCK_ACQUIRED',
        cpid: this.#cpid,
        wsUrl: this.#wsUrl,
        tabId: this.#tabId,
      });
      return { acquired: true };
    }

    // First attempt lost the CAS — inspect the holder.
    const holder = await this.#repo.get(this.#cpid, this.#wsUrl);
    if (!holder) {
      // Row vanished between attempts (reaper, race). One more try.
      const retry = await this.#tryInsertOwnRow();
      if (retry) {
        this.#held = true;
        this.#broadcast?.post({
          type: 'LOCK_ACQUIRED',
          cpid: this.#cpid,
          wsUrl: this.#wsUrl,
          tabId: this.#tabId,
        });
        return { acquired: true };
      }
      return { acquired: false };
    }

    if (holder.tabId === this.#tabId) {
      // It's our own previous lock — take it over without waiting for the
      // reaper. release() will only succeed because we own the row.
      const released = await this.#repo.release(this.#cpid, this.#wsUrl, this.#tabId);
      if (!released) {
        // Should never happen unless someone wrote a different tabId between
        // the get() and the release(); fall through and report the holder.
        return { acquired: false, holder };
      }
      const reacquired = await this.#tryInsertOwnRow();
      if (!reacquired) {
        const newHolder = await this.#repo.get(this.#cpid, this.#wsUrl);
        return newHolder ? { acquired: false, holder: newHolder } : { acquired: false };
      }
      this.#held = true;
      this.#broadcast?.post({
        type: 'LOCK_ACQUIRED',
        cpid: this.#cpid,
        wsUrl: this.#wsUrl,
        tabId: this.#tabId,
      });
      return { acquired: true };
    }

    return { acquired: false, holder };
  }

  async #tryInsertOwnRow(): Promise<boolean> {
    const now = this.#now();
    const row: TabLockRow = {
      cpid: this.#cpid,
      wsUrl: this.#wsUrl,
      tabId: this.#tabId,
      heldSince: now,
      lastHeartbeat: now,
      ocppConnected: false,
    };
    return this.#repo.tryAcquire(row);
  }

  /** Start the periodic heartbeat. Must be called after a successful acquire. */
  startHeartbeat(): void {
    if (!this.#held || this.#heartbeatHandle !== null) return;
    this.#heartbeatHandle = setInterval(() => {
      void this.#repo.heartbeat(this.#cpid, this.#wsUrl, this.#tabId, this.#now());
    }, this.#intervalMs);
  }

  stopHeartbeat(): void {
    if (this.#heartbeatHandle === null) return;
    clearInterval(this.#heartbeatHandle);
    this.#heartbeatHandle = null;
  }

  /** One-shot heartbeat — useful for tests and the initial post-acquire pulse. */
  async beat(): Promise<boolean> {
    return this.#repo.heartbeat(this.#cpid, this.#wsUrl, this.#tabId, this.#now());
  }

  async release(): Promise<void> {
    this.stopHeartbeat();
    if (!this.#held) return;
    const released = await this.#repo.release(this.#cpid, this.#wsUrl, this.#tabId);
    this.#held = false;
    if (released) {
      this.#broadcast?.post({
        type: 'LOCK_RELEASED',
        cpid: this.#cpid,
        wsUrl: this.#wsUrl,
        tabId: this.#tabId,
      });
    }
  }

  /** Mark "OCPP-connected" on the existing lock row (informational only). */
  async markOcppConnected(connected: boolean): Promise<void> {
    if (!this.#held) return;
    await this.#repo.setOcppConnected(this.#cpid, this.#wsUrl, this.#tabId, connected);
  }

  /** Convenience accessor (mostly for diagnostics). */
  get userAgent(): string | undefined {
    return this.#userAgent;
  }
}
